import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { CheckIcon } from "lucide-react-native";
import { toast } from "sonner-native";

import { BrokerConsentAnswerButtons } from "@/blocks/BrokerConsentAnswerButtons";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnIcon } from "@/components/VnIcon";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnScrollView } from "@/components/VnScrollView";
import { VnText } from "@/components/VnText/VnText";
import { useCreatePatientConsentMutation } from "@/data/mutations/consentsMutations";
import { useListApplicationsQuery } from "@/data/queries/applicationsQueries";
import { usePendingConsentsQuery } from "@/data/queries/consentsQueries";
import {
  BrokerConsentAnswerType,
  getBrokerConsentCopy,
} from "@/domain/brokerConsent";
import { useTranslator } from "@/hooks/useTranslate";
import { getAgentAssistedProgramsLabel } from "@/utils/applications";
import { logger } from "@/utils/logger";

export const ApplicationConfirmationScreen = () => {
  const router = useRouter();
  const { t } = useTranslator();
  const { applicationIds } = useLocalSearchParams<{
    applicationIds?: string;
  }>();

  const {
    data: submittedApplications,
    isSuccess: isApplicationsSuccess,
    isFetching: isFetchingApplications,
    isFetchedAfterMount: isApplicationsFetchedAfterMount,
  } = useListApplicationsQuery({ filters: { status: "SUBMITTED" } });
  const {
    data: pendingConsents,
    isSuccess: isPendingConsentsSuccess,
    isFetching: isFetchingPendingConsents,
    isFetchedAfterMount: isPendingConsentsFetchedAfterMount,
  } = usePendingConsentsQuery();
  const { mutateAsync: createPatientConsent } =
    useCreatePatientConsentMutation();

  const [selectedAnswer, setSelectedAnswer] =
    useState<BrokerConsentAnswerType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Consenting on step 1 invalidates a query nothing was observing, and the
  // dashboard warms the same submitted-applications cache.
  const isAwaitingFreshBrokerConsentState =
    (isFetchingApplications && !isApplicationsFetchedAfterMount) ||
    (isFetchingPendingConsents && !isPendingConsentsFetchedAfterMount);

  // Failed refetches retain cached results and count as fetched after mount.
  const hasFreshBrokerConsentState =
    isApplicationsFetchedAfterMount &&
    isApplicationsSuccess &&
    isPendingConsentsFetchedAfterMount &&
    isPendingConsentsSuccess;

  const justSubmittedIds = applicationIds?.split(",").filter(Boolean) ?? [];
  const brokerConsentCopy = getBrokerConsentCopy(
    submittedApplications
      ?.filter(application => justSubmittedIds.includes(application.id))
      .map(application => application.applicationType) ?? [],
  );

  // BROKER_HANDOFF stays pending until the patient consents, so still finding it
  // here after an agent-assisted submission means they declined. A failed lookup
  // falls through rather than block a submission that already succeeded.
  const brokerDeclinedCopy =
    hasFreshBrokerConsentState &&
    brokerConsentCopy &&
    pendingConsents?.pendingConsents.includes("BROKER_HANDOFF")
      ? brokerConsentCopy
      : null;

  const navigateToNextScreen = () => {
    if (applicationIds) {
      router.replace({
        pathname: "/(authed)/(modal)/application/nps-survey",
        params: { applicationIds },
      });

      return;
    }

    router.replace("/(authed)");
  };

  const handleContinue = async () => {
    if (brokerDeclinedCopy && selectedAnswer) {
      setIsSubmitting(true);
      try {
        await createPatientConsent({
          consentType: "BROKER_HANDOFF",
          isConsented: selectedAnswer === "yes",
        });
      } catch (error) {
        // Leaving now would silently drop an answer the patient just gave.
        logger.error({
          message: "Failed to record the reconsidered broker consent",
          error,
        });
        toast.error(t("common:errors.unknown"));

        return;
      } finally {
        setIsSubmitting(false);
      }
    }

    navigateToNextScreen();
  };

  return (
    <VnBox className="bg-background h-full">
      <VnScrollView className="flex-1" contentContainerClassName="items-center">
        <VnBox className="w-full max-w-lg px-6">
          <VnBox className="items-center gap-6 pb-8 pt-20 md:pt-24">
            <VnBox className="bg-sidebar items-center justify-center rounded-full p-5">
              <VnIcon as={CheckIcon} size={40} className="text-secondary" />
            </VnBox>
            <VnBox className="items-center gap-3">
              <VnText
                variant="headerOne"
                className="text-center"
                i18nKey="native:applicationConfirmationScreen.title"
              />
              {isAwaitingFreshBrokerConsentState ? (
                <VnLoadingSpinner />
              ) : (
                <VnText
                  variant="lg"
                  className="text-muted-foreground text-center"
                  i18nKey={
                    brokerDeclinedCopy?.submissionSummaryI18nKey ??
                    "native:applicationConfirmationScreen.description"
                  }
                  i18nOptions={
                    brokerDeclinedCopy
                      ? {
                          programs: getAgentAssistedProgramsLabel(
                            brokerDeclinedCopy.agentAssistedPrograms,
                          ),
                        }
                      : undefined
                  }
                />
              )}
            </VnBox>
          </VnBox>
          {brokerDeclinedCopy && (
            <VnCard className="gap-4">
              <VnText
                variant="headerTwo"
                i18nKey="native:applicationConfirmationScreen.brokerDeclined.title"
              />
              <VnText
                variant="lg"
                className="text-muted-foreground"
                i18nKey="native:applicationConfirmationScreen.brokerDeclined.description"
              />
              <BrokerConsentAnswerButtons
                selectedAnswer={selectedAnswer}
                isDisabled={isSubmitting}
                onAnswerPress={setSelectedAnswer}
              />
            </VnCard>
          )}
        </VnBox>
      </VnScrollView>
      <VnBox className="border-border bg-background items-center border-t px-5 py-4">
        <VnButton
          className="w-full max-w-lg"
          i18nKey="native:applicationConfirmationScreen.continueButton"
          isLoading={isSubmitting}
          isDisabled={
            isAwaitingFreshBrokerConsentState ||
            (Boolean(brokerDeclinedCopy) && !selectedAnswer)
          }
          onPress={() => {
            void handleContinue();
          }}
        />
      </VnBox>
    </VnBox>
  );
};
